import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Input, Select, Button, Upload, message } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { INDUSTRY_OPTIONS } from "common/enums/Industry.enum";
import { API_BASE_URL, API_ENDPOINTS } from "config/apiConfig";
import { PathStoreEnum } from "common/enums/PathStore.enum";
import { CompanyInfoType } from "common/types/companyInfo.type";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { clearInformationData } from "reducers/informationSlice";
import { CompanyInfoStatusEnum } from "common/enums/CompanyInfoStatus.enum";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCommonContext } from "contexts/CommonContext";
import { cleanData } from "reducers/authSlice";
import { isEmpty, isEqual } from "lodash";
import { MAX_FILE_SIZE } from "common/constants";
import InformationTabsLayout from "components/companyProfile/InformationTabsLayout";
import {
  UpdateCompanyProfileRequest,
  useGetCompanyProfileQuery,
  useUpdateCompanyProfileMutation,
} from "reducers/companyProfileApi";

const SellerRegisterInfo = () => {
  const { setHeaderTitle } = useCommonContext();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [pdfFile, setPdfFile] = useState<string | null>("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { userData } = useSelector((state: RootState) => state.auth);
  const [searchParams] = useSearchParams();
  const profileCompanyCode = searchParams.get("companyCode") || userData.code;
  const canEditProfileAvatar = profileCompanyCode === userData.code;
  const profileRole = canEditProfileAvatar ? "seller" : "buyer";
  const navigate = useNavigate();
  const { data: companyProfileData } = useGetCompanyProfileQuery(
    { companyCode: userData.code },
    { skip: !userData.code },
  );
  const [updateCompanyProfile] = useUpdateCompanyProfileMutation();
  const companyCurrentDetail = companyProfileData?.data;
  const [isChanged, setIsChanged] = useState(false);
  const initialData = {
    company_name: "",
    postal_code: "",
    prefecture: "",
    address: "",
    building_room: "",
    industry: "",
    contact_last_name: "",
    contact_first_name: "",
    phone_number: "",
    email: "",
  };

  // useEffect
  useEffect(() => {
    setHeaderTitle("登録情報の管理");
  }, []);

  const fetchUpdateCompanyInfo = async (data: UpdateCompanyProfileRequest) => {
    try {
      await updateCompanyProfile({
        companyCode: userData.code,
        data: {
          ...data,
          status: CompanyInfoStatusEnum.PENDING_REVIEW,
        },
      }).unwrap();
      message.success("情報を更新しました");
      dispatch(cleanData());
      dispatch(clearInformationData());
      navigate("/");
    } catch {
      message.error("情報の更新に失敗しました");
    }
  };

  const originalData: CompanyInfoType = useMemo(
    () => ({
      company_name: companyCurrentDetail?.company_name,
      postal_code: companyCurrentDetail?.postal_code,
      prefecture: companyCurrentDetail?.prefecture,
      address: companyCurrentDetail?.address,
      building_room: companyCurrentDetail?.building_room,
      industry: companyCurrentDetail?.industry,
      contact_last_name: companyCurrentDetail?.contact_last_name,
      contact_first_name: companyCurrentDetail?.contact_first_name,
      phone_number: companyCurrentDetail?.phone_number,
      email: companyCurrentDetail?.email,
    }),
    [companyCurrentDetail],
  );

  // Handle actions
  const handleSave = async (values: CompanyInfoType) => {
    if (isEqual(values, originalData) && isEmpty(fileList)) {
      return;
    }
    await fetchUpdateCompanyInfo({
      ...values,
      corp_regist_cert_url: pdfFile,
    });
  };

  const resetUnsavedChanges = useCallback(() => {
    form.resetFields();
    if (companyCurrentDetail) {
      form.setFieldsValue(originalData);
    }

    setErrors({});
    setPdfFile(""); // Reset PDF file to empty string
    setFileList([]); // Clear upload file list
  }, [companyCurrentDetail, form, originalData]);

  useEffect(() => {
    resetUnsavedChanges();
  }, [resetUnsavedChanges]);

  const handleCancel = () => {
    resetUnsavedChanges();
    message.info("変更をキャンセルしました");
  };

  // function support
  const upload: UploadProps = {
    name: "file",
    action: `${API_BASE_URL}${API_ENDPOINTS.UPLOAD_FILE}?path_store=${PathStoreEnum.PATH_STORE_PDF}`,
    maxCount: 1,
    accept: ".pdf",
    fileList: fileList,
    beforeUpload(file) {
      if (file.size > MAX_FILE_SIZE) {
        message.error("※PDF推奨（最大10MB）");
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange(info) {
      setFileList(info.fileList);
      if (info.file.status === "done") {
        message.success(`${info.file.name}をアップロードしました`);
        setPdfFile(info.file.response.data.path);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name}のアップロードに失敗しました`);
      }
    },
  };

  useEffect(() => {
    if (!companyCurrentDetail) return;
    if (isEqual(formValues, originalData) && isEmpty(fileList)) {
      setIsChanged(false);
    } else {
      setIsChanged(true);
    }
  }, [formValues, companyCurrentDetail, fileList]);

  return (
    <InformationTabsLayout
      canEditAvatar={canEditProfileAvatar}
      companyCode={profileCompanyCode}
      onReturnToInfo={resetUnsavedChanges}
      profileOnly={!canEditProfileAvatar}
      role={profileRole}
    >
      <div className="flex w-full flex-col items-start gap-[30px] md:gap-12">
        <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                    登録情報
                  </div>
                </div>
              </div>
            </div>

            <Form
              form={form}
              initialValues={initialData}
              onFinish={handleSave}
              className="w-full max-w-[800px]"
            >
              <div className="self-stretch bg-white flex flex-col justify-start items-start gap-3.5">
                {/* 法人名 */}
                <div className="self-stretch md:inline-flex justify-start items-start gap-5">
                  <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      法人名
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1 inline-flex flex-col justify-start items-start gap-[5px]">
                    <Form.Item
                      name="company_name"
                      className="!mb-0 w-full"
                      rules={[
                        { required: true, message: "法人名を入力してください" },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.company_name ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                    <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal leading-tight">
                      ※変更には再審査が必要です
                    </div>
                  </div>
                </div>

                {/* 郵便番号 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      郵便番号
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex justify-start items-center gap-3.5">
                    <Form.Item
                      name="postal_code"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "郵便番号を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="123-4567"
                        status={errors.postal_code ? "error" : undefined}
                        className="!h-12 w-48"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 都道府県 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      都道府県
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="prefecture"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "都道府県を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.prefecture ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 市区町村・番地 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      市区町村・番地
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="address"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "市区町村・番地を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.address ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 建物名・部屋番号 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      建物名・部屋番号
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item name="building_room" className="!mb-0">
                      <Input
                        size="large"
                        status={errors.building_room ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 業種 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      業種
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="industry"
                      className="!mb-0"
                      rules={[
                        { required: true, message: "業種を選択してください" },
                      ]}
                    >
                      <Select
                        size="large"
                        options={Object.values(INDUSTRY_OPTIONS)}
                        status={errors.industry ? "error" : undefined}
                        className="!h-12"
                        placeholder="業種を選択"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 担当者名 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      担当者名
                    </div>
                  </div>
                  <div className="flex-1 flex justify-start items-center gap-2.5">
                    <Form.Item
                      name="contact_last_name"
                      className="!mb-0 flex-1"
                      rules={[
                        { required: true, message: "姓を入力してください" },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="姓"
                        status={errors.contact_last_name ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                    <Form.Item
                      name="contact_first_name"
                      className="!mb-0 flex-1"
                      rules={[
                        { required: true, message: "名を入力してください" },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder="名"
                        status={errors.contact_first_name ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 電話番号 */}
                <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                  <div className="w-44 flex justify-start items-center gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      電話番号
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1">
                    <Form.Item
                      name="phone_number"
                      className="!mb-0"
                      rules={[
                        {
                          required: true,
                          message: "電話番号を入力してください",
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        status={errors.phone_number ? "error" : undefined}
                        className="!h-12"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* メールアドレス */}
                <div className="self-stretch md:inline-flex justify-start items-start gap-5">
                  <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                    <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                      メールアドレス
                    </div>
                  </div>
                  <div className="w-full md:w-auto flex-1 inline-flex flex-col justify-start items-start gap-[5px]">
                    <Form.Item name="email" className="!mb-0 w-full">
                      <Input
                        size="large"
                        disabled
                        className="!h-12 !bg-neutral-50"
                      />
                    </Form.Item>
                    <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal leading-tight">
                      ※ログインIDとして使用されます（変更不可）
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          </div>

          {/* File Upload Section */}
          <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
            <div
              data-プロパティ1="base"
              className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
            >
              <div className="w-1 self-stretch bg-primary rounded-sm" />
              <div className="flex-1 flex justify-start items-center gap-2.5">
                <div className="flex justify-start items-center gap-[5px]">
                  <div className="justify-start text-primary text-[18px] font-bold leading-9">
                    本人確認書類の再提出（必要な場合）
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch bg-white flex flex-col justify-center items-center gap-6">
              <div className="self-stretch justify-start text-black text-[15px] md:text-base font-normal leading-relaxed">
                法人名などを変更した場合、再審査となるため以下の書類の再提出が必要です。
              </div>
              <div className="self-stretch flex flex-col justify-start items-start gap-5">
                <div className="self-stretch justify-start text-primary text-[18px] font-bold leading-loose">
                  法人登記簿謄本（履歴事項全部証明書）
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
                  <div className="self-stretch justify-start text-black text-[15px] md:text-base font-normal leading-relaxed">
                    <ul className="list-disc pl-5">
                      <li>発行から3ヶ月以内のものをご用意ください</li>
                      <li>スキャンまたはPDF形式で提出してください</li>
                    </ul>
                  </div>
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-1.5">
                  <div className="flex items-center gap-[10px] self-stretch register-info-upload">
                    <Upload {...upload}>
                      <Button className="h-10 min-w-40 px-5 bg-white rounded-md outline outline-2 outline-offset-[-2px] outline-primary flex justify-center items-center gap-[5px] !text-primary !font-bold hover:!bg-primary-light">
                        ファイルを選択
                      </Button>
                      {isEmpty(fileList) && (
                        <div className="text-neutral-muted text-center font-['Noto_Sans_JP'] text-sm font-normal leading-[160%]">
                          選択されていません。
                        </div>
                      )}
                    </Upload>
                  </div>
                  <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal leading-tight">
                    ※PDF推奨（最大10MB）
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="self-stretch flex flex-col justify-start items-start gap-[15px] md:gap-3.5">
          <div className="self-stretch flex flex-col-reverse md:flex-row md:inline-flex justify-start items-start gap-[10px] md:gap-3.5">
            <Button
              type="default"
              size="large"
              onClick={handleCancel}
              className="w-80 min-h-14 px-5 py-2.5 bg-primary-light rounded-md !text-primary !font-bold !text-[16px] md:!text-lg hover:!bg-primary-light/80"
            >
              変更をキャンセル
            </Button>
            <Button
              disabled={!isChanged}
              type="primary"
              size="large"
              onClick={() => form.submit()}
              className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md text-white !font-bold !text-[16px] md:!text-lg"
            >
              情報を保存する
            </Button>
          </div>
          <div className="self-stretch justify-start text-black text-[11px] md:text-xs font-normal leading-tight">
            ※法人名の変更など、審査が必要な内容については別途通知をお送りします。
          </div>
        </div>
      </div>
    </InformationTabsLayout>
  );
};

export default SellerRegisterInfo;
